import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAggregatedRef } from '@tanium/react-use-aggregated-ref';
import { useResizeObserver } from '@tanium/react-use-resize-observer';
import { DEFAULT_DIRECTION, DEFAULT_MIN_VISIBLE_ITEMS } from './constants';
import { OverflowDirection } from './types';

const useStableCallback = (callback: () => void) => {
  const callbackRef = useRef(callback);

  useLayoutEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  return useCallback(() => callbackRef.current(), []);
};

/**
 *  Min and max represent the range of values in which the optimal
 * `visibleChildCount` may lie. Both min and max are inclusive.
 */
interface MeasurementStatus {
  min: number;
  max: number;
}

const checkOverflowing = (container: HTMLElement, direction: OverflowDirection) => {
  const isWidth = direction === 'row';

  const scroll = isWidth ? container.scrollWidth : container.scrollHeight;
  const client = isWidth ? container.clientWidth : container.clientHeight;

  return scroll > client;
};

export interface UseDiscreteOverflowConfig {
  /**
   * Whether the overflow behavior is enabled. When `false`, the hook has no
   * effect and will return the number of items in the provided array as the
   * number of visible items.
   *
   * Defaults to `true`.
   */
  isEnabled?: boolean;
  /**
   * The direction in which the overflow handling occurs.
   *
   * Defaults to `row`.
   */
  direction?: OverflowDirection;
  /**
   * How many items will be forced to render when space is very constrained.
   *
   * Defaults to `1`.
   */
  minVisibleItems?: number;
}

/**
 * Hook to find the optimal number of elements that will fit in a container. The
 * hook is not prescriptive about _how_ those elements are laid out in the
 * container or whether there are other elements in the container.
 *
 * @see `<DiscreteOverflow />` which also handles rendering the UI.
 *
 * @param childItems The items which will be rendered in the container
 * @param config Configuration options for the overflow behavior
 * @returns The number of items to show and a ref for the container
 */
export const useDiscreteOverflow = (
  childItems: readonly unknown[],
  config: UseDiscreteOverflowConfig = {},
) => {
  const {
    isEnabled = true,
    direction = DEFAULT_DIRECTION,
    minVisibleItems: minVisibleItemsOption = DEFAULT_MIN_VISIBLE_ITEMS,
  } = config;
  const minVisibleItems = Math.max(0, minVisibleItemsOption);

  const containerRef = useRef<HTMLElement>(null);
  const [visibleChildCount, setVisibleChildCount] = useState(0);
  const measurementStatusRef = useRef<MeasurementStatus>({
    min: minVisibleItems,
    max: Infinity,
  });

  const measure = useCallback(() => {
    if (!isEnabled || !containerRef.current) {
      return;
    }

    const clamp = (value: number) =>
      Math.min(measurementStatusRef.current.max, Math.max(measurementStatusRef.current.min, value));

    if (checkOverflowing(containerRef.current, direction)) {
      measurementStatusRef.current.max = clamp(visibleChildCount - 1);
    } else {
      measurementStatusRef.current.min = visibleChildCount;
    }

    // Rounding must be done via `ceil` in order for min and max to eventually
    // converge. The max can represent either "another child might fit" or
    // "another child will fit". When the difference between min and max is 1,
    // choosing the min as the next count would never result in differentiating
    // those two scenarios. Choosing the max (rounding up) results in a
    // measurement which can distinguish between the two.
    const nextCount = Math.ceil(
      (measurementStatusRef.current.min + measurementStatusRef.current.max) / 2,
    );

    if (nextCount !== visibleChildCount) {
      setVisibleChildCount(nextCount);
    }
  }, [direction, isEnabled, visibleChildCount]);

  useLayoutEffect(() => {
    // When the children or direction change, the measurement status needs to be
    // completely reset. A change of `minVisibleItems` doesn't inherently
    // require a full reset, but the implementation is simplified by doing so.
    measurementStatusRef.current = {
      max: childItems.length,
      min: minVisibleItems,
    };
    setVisibleChildCount(minVisibleItems);
  }, [childItems, direction, minVisibleItems]);

  useLayoutEffect(() => {
    measure();
    // force re-measuring when childItems change, otherwise visibleChildCount
    // becomes stale if container doesn't resize
  }, [measure, childItems]);

  const containerResizeRef = useResizeObserver<HTMLElement>(
    useStableCallback(() => {
      // When the container's size changes, `visibleChildCount` could be reset,
      // but it doesn't need to be. Instead, the prior value is used as a
      // starting point for the overflow search with the assumption that it's
      // probably close to the new value. `measurementStatusRef` does need to
      // be reset because it's no longer known whether the current visible
      // children overflow the container.
      measurementStatusRef.current = {
        max: childItems.length,
        min: minVisibleItems,
      };
      measure();
    }),
  );

  const containerRefs = useMemo(
    () => [containerRef, containerResizeRef],
    [containerRef, containerResizeRef],
  );
  const aggregatedContainerRefs = useAggregatedRef(containerRefs);

  return [isEnabled ? visibleChildCount : childItems.length, aggregatedContainerRefs] as const;
};
